import { colors, fonts, motion, sizes, space } from '../../../../theme/tokens'
import { GREEN } from '../../../../components/choice-look'

export const styles = theme => ({
  page: {
    background: '#f5f7fa',
    minHeight: '100%',
    padding: '24px 16px 48px',
    '@media (min-width: 960px)': {
      padding: '40px 32px 64px'
    }
  },
  layout: {
    margin: '0 auto',
    maxWidth: 560
  },
  card: {
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 1px 3px rgba(15,23,42,0.04), 0 4px 16px rgba(15,23,42,0.04)',
    padding: '24px 20px 28px',
    '@media (min-width: 600px)': {
      padding: '32px 36px 40px'
    }
  },
  // Embedded in the Book & pay screen: the host card supplies the chrome.
  embedded: { width: '100%' },
  embeddedCard: { background: 'transparent', padding: 0 },

  // Top one-line order summary (replaces the old inline full breakdown)
  orderHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    gap: 12,
    paddingBottom: 18,
    borderBottom: '1px solid #eef0f3',
    marginBottom: 24
  },
  orderHeaderEyebrow: {
    fontFamily: "Poppins, 'Helvetica Neue', sans-serif",
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#94a3b8',
    marginBottom: 4
  },
  orderHeaderName: {
    fontFamily: "Poppins, 'Helvetica Neue', sans-serif",
    fontSize: 16,
    fontWeight: 600,
    color: '#0f172a',
    lineHeight: 1.25
  },
  orderHeaderTotal: {
    fontFamily: "Poppins, 'Helvetica Neue', sans-serif",
    fontSize: 22,
    fontWeight: 700,
    color: '#0f172a',
    whiteSpace: 'nowrap'
  },

  // Sections
  section: {
    paddingTop: 4,
    marginBottom: 24
  },
  sectionHeading: {
    fontFamily: "Poppins, 'Helvetica Neue', sans-serif",
    fontSize: 18,
    fontWeight: 600,
    color: '#0f172a',
    margin: '0 0 14px 0'
  },

  // Field treatments - match step-address styling
  fieldStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 14,
    '@media (min-width: 600px)': {
      gridTemplateColumns: '1fr 1fr'
    }
  },
  fieldLabel: {
    fontFamily: "Poppins, 'Helvetica Neue', sans-serif",
    fontSize: 13,
    fontWeight: 500,
    color: '#334155',
    marginBottom: 6,
    display: 'block'
  },
  field: {
    background: '#fff',
    border: '1.5px solid #e2e8f0',
    borderRadius: 10,
    padding: '4px 14px',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    '&:focus-within': {
      borderColor: '#37b45a',
      boxShadow: '0 0 0 3px rgba(55, 180, 90, 0.15)'
    },
    '& .MuiTextField-root, & .MuiFormControl-root': {
      width: '100%'
    },
    '& .MuiInputBase-root': {
      width: '100%',
      fontFamily: "Poppins, 'Helvetica Neue', sans-serif",
      fontSize: 15,
      color: '#0f172a',
      padding: '8px 0'
    },
    '& .MuiInputBase-input': {
      flex: 1,
      width: '100%',
      padding: '6px 0'
    },
    '& .MuiInputBase-input::placeholder': {
      color: '#94a3b8',
      opacity: 1
    },
    '& .MuiFormLabel-root': { display: 'none' }
  },

  divider: {
    display: 'flex',
    alignItems: 'center',
    margin: '4px 0',
    '&::before, &::after': {
      content: '""',
      flex: 1,
      borderBottom: '1px solid #eef0f3'
    },
    '& span': {
      padding: '0 12px',
      fontFamily: "Poppins, 'Helvetica Neue', sans-serif",
      fontSize: 12,
      color: '#94a3b8',
      textTransform: 'uppercase',
      letterSpacing: '0.06em'
    }
  },

  // Quiet inline hint - soft red text + small icon, no background / border.
  // Reads as "by the way" not "STOP!". Replaces the old bottom-right Snackbar
  // and the heavy red banner that lived here previously.
  bookingRecap: {
    position: 'relative',
    background: '#f8fafc',
    border: '1px solid #eef0f3',
    borderRadius: 12,
    padding: '12px 16px',
    margin: '0 0 18px',
    display: 'flex',
    flexDirection: 'column',
    gap: 6
  },
  bookingRecapRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    fontFamily: "Poppins, 'Helvetica Neue', sans-serif",
    fontSize: 13,
    fontWeight: 500,
    color: '#334155',
    lineHeight: 1.4,
    paddingRight: 48 // leave room for the Edit link in the top-right
  },
  bookingRecapIcon: {
    color: '#64748b',
    flexShrink: 0,
    marginTop: 1
  },
  bookingRecapEdit: {
    position: 'absolute',
    top: 10,
    right: 12,
    fontFamily: "Poppins, 'Helvetica Neue', sans-serif",
    fontSize: 12,
    fontWeight: 600,
    color: '#2ABFF3',
    textDecoration: 'none',
    padding: '4px 6px',
    borderRadius: 6,
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  errorPill: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: '4px 2px 14px',
    color: '#dc2626',
    fontFamily: "Poppins, 'Helvetica Neue', sans-serif",
    fontSize: 13,
    fontWeight: 500,
    lineHeight: 1.4,
    '& > svg': {
      flexShrink: 0,
      marginTop: 2,
      width: 16,
      height: 16
    }
  },
  // Stripe.js failed to load: a calm note above the card button, not an error.
  stripeFallback: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    margin: '0 0 12px',
    padding: '10px 12px',
    borderRadius: 10,
    background: '#f1f5f9',
    color: '#334155',
    fontFamily: "Poppins, 'Helvetica Neue', sans-serif",
    fontSize: 13,
    fontWeight: 500,
    lineHeight: 1.4,
    '& > svg': {
      flexShrink: 0,
      marginTop: 1,
      width: 16,
      height: 16
    }
  },
  errorRetry: {
    marginLeft: 'auto',
    background: 'transparent',
    border: 'none',
    color: '#dc2626',
    fontFamily: "Poppins, 'Helvetica Neue', sans-serif",
    fontSize: 13,
    fontWeight: 600,
    textDecoration: 'underline',
    cursor: 'pointer',
    padding: '0 2px',
    flexShrink: 0,
    '&:hover': { color: '#991b1b' }
  },

  signedInNote: {
    fontFamily: "Poppins, 'Helvetica Neue', sans-serif",
    fontSize: 13,
    color: '#475569',
    margin: 0,
    '& strong': { color: '#0f172a', fontWeight: 600 }
  },

  // Compact row for promo code + tip toggles
  compactRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 14,
    margin: '4px 0 18px'
  },
  // A refused code, right under the code field (26 Oct 2026): a line of
  // its own in the row and in the small print, the error colour, no box.
  promoNotice: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: 6,
    flexBasis: '100%',
    width: '100%',
    margin: '2px 0 0',
    color: colors.error,
    fontFamily: "Poppins, 'Helvetica Neue', sans-serif",
    fontSize: 13,
    fontWeight: 500,
    lineHeight: '18px',
    textAlign: 'left',
    '& > svg': { flexShrink: 0, marginTop: 1, width: 16, height: 16 }
  },
  promoInline: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%'
  },

  // Book & pay: the one-line WhatsApp opt-in above the buttons, and the
  // small print under them (booking-screen-trim, 9 Oct 2026).
  whatsappLine: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    minHeight: 44,
    margin: '4px 0 8px',
    fontFamily: "Poppins, 'Helvetica Neue', sans-serif",
    fontSize: 13,
    color: '#475569',
    cursor: 'pointer',
    '& input': { width: 18, height: 18, flexShrink: 0, accentColor: '#37b45a' }
  },
  smallPrint: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    marginTop: 8,
    textAlign: 'center'
  },
  termsLine: {
    margin: 0,
    fontFamily: "Poppins, 'Helvetica Neue', sans-serif",
    fontSize: 12,
    lineHeight: 1.45,
    color: '#64748b',
    '& a': { color: '#14a0d1' }
  },

  // Buttons
  linkButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    fontFamily: "Poppins, 'Helvetica Neue', sans-serif",
    fontSize: 13,
    fontWeight: 500,
    color: '#14a0d1',
    cursor: 'pointer',
    textAlign: 'left',
    // 44 tall: "Have a promo code?" and the tip were 15px tap targets on a
    // phone (QA, 9 Oct 2026; DESIGN.md §3 --tap-min).
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: 44,
    '&:hover': { color: '#0f7da4' },
    '& strong': { fontWeight: 600 }
  },
  primaryGhostBtn: {
    background: '#fff',
    color: '#37b45a',
    border: '1.5px solid #37b45a',
    borderRadius: 10,
    padding: '10px 18px',
    fontFamily: "Poppins, 'Helvetica Neue', sans-serif",
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.12s ease, transform 0.12s ease',
    '&:hover': { background: 'rgba(55,180,90,0.06)' },
    '&:active': { transform: 'scale(0.98)' }
  },

  // Terms row
  termsRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: '6px 0 14px',
    cursor: 'pointer',
    userSelect: 'none'
  },
  termsText: {
    fontFamily: "Poppins, 'Helvetica Neue', sans-serif",
    fontSize: 13,
    color: '#475569',
    lineHeight: 1.4,
    paddingTop: 6,
    '& a': {
      color: '#14a0d1',
      textDecoration: 'underline',
      '&:hover': { color: '#0f7da4' }
    }
  },

  signedInNoteRow: {
    padding: '0 0 14px',
    marginBottom: 14,
    borderBottom: '1px solid #eef0f3'
  },

  // Payment section
  payment: {
    paddingTop: 8
  },
  // The one embedded checkout (one-checkout.js, EMBEDDED_CHECKOUT).
  oneCheckout: {
    display: 'flex',
    flexDirection: 'column'
  },
  // Holds the Express Checkout Element's iframe; the height stops the card
  // form jumping when Apple Pay arrives.
  // The one wallet button's slot, held at exactly its height (expressOptions
  // buttonHeight 54) from the first paint, so the button arriving with
  // Stripe.js moves nothing below it (12 Oct 2026, Robin: "fuck fix all of
  // that then pls.."). A fixed height, not a minimum: whatever Stripe's
  // iframe adds round the button cannot push the page. Gone only when
  // Stripe says there is no wallet (oneExpressNone), and then the card form
  // opens in its place anyway.
  oneExpress: {
    height: 54,
    marginBottom: space(12),
    transition: 'opacity 0.15s ease'
  },
  // The unpaid line right above the wallet button, for as long as the
  // booking is unpaid (one-checkout.js, lib/unpaid-status.js): "Next: your
  // car size", "All set. Tap Apple Pay to book.". Calm: the grey of helper
  // text (19 Oct 2026, Robin: "its a bit offputting atm?"); red after a
  // refused press while something is still missing; the brand green once a
  // refused wallet tap's fields are filled in ("Now tap").
  unpaidStatus: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: space(8),
    fontFamily: fonts.body,
    fontSize: 15,
    fontWeight: 400,
    lineHeight: '20px',
    color: colors.mute,
    margin: `0 0 ${space(8)}px`,
    textAlign: 'center',
    '& > svg': { flexShrink: 0, marginTop: 2, width: 16, height: 16 }
  },
  unpaidStatusAlert: { color: colors.error, textAlign: 'left', justifyContent: 'flex-start', '& $unpaidStatusLink': { color: 'inherit' } },
  unpaidStatusNudge: { color: GREEN, fontWeight: 600 },
  // The next thing is a link to it (an accent link, DESIGN.md §1).
  unpaidStatusLink: {
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    fontWeight: 500,
    color: colors.accent,
    textAlign: 'inherit',
    textDecoration: 'underline',
    textUnderlineOffset: '3px',
    cursor: 'pointer'
  },
  oneExpressNone: {
    height: 0,
    marginBottom: 0,
    overflow: 'hidden'
  },
  oneCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: space(12),
    marginTop: space(4)
  },
  // The card section's accordion: a 0fr to 1fr grid row animates to the
  // content's own height, which a max-height guess cannot. Hidden from focus
  // and touch while shut; visibility flips at the end of the close.
  oneCollapse: {
    display: 'grid',
    gridTemplateRows: '0fr',
    visibility: 'hidden',
    transition: `grid-template-rows ${motion.base} ease, visibility 0s linear ${motion.base}`,
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
  },
  oneCollapseOpen: {
    gridTemplateRows: '1fr',
    visibility: 'visible',
    transition: `grid-template-rows ${motion.base} ease, visibility 0s`
  },
  oneCardBelowTrigger: {
    marginTop: space(12)
  },
  oneCollapseInner: {
    minHeight: 0,
    overflow: 'hidden',
    // Room for the email field's focus ring inside the clip.
    padding: '0 2px',
    margin: '0 -2px'
  },
  walletWrap: {
    marginBottom: 14,
    transition: 'opacity 0.15s ease'
  },
  cardForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    paddingTop: 4
  },
  cardFormHeading: {
    fontFamily: "Poppins, 'Helvetica Neue', sans-serif",
    fontSize: 14,
    fontWeight: 600,
    color: '#0f172a',
    margin: '0 0 4px 0'
  },
  cardTriggerBtn: {
    width: '100%',
    height: 50,
    borderRadius: 12,
    border: '1.5px solid #e2e8f0',
    background: '#ffffff',
    color: '#0f172a',
    fontFamily: "Poppins, 'Helvetica Neue', sans-serif",
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.12s ease, border-color 0.12s ease, transform 0.12s ease',
    '&:hover:not(:disabled)': {
      background: '#f8fafc',
      borderColor: '#cbd5e1'
    },
    '&:active:not(:disabled)': {
      transform: 'scale(0.99)'
    }
  },
  // "Or pay by card" under Apple Pay or Google Pay (17 Oct 2026): a quiet
  // text button, so the wallet stays the one thing that looks like the way
  // to pay. Robin: "I dont want to downgrade apple pay as I think its how
  // everyone wants to pay.. on iphone etc.." Still a full-width tap target.
  cardTriggerQuiet: {
    height: sizes.tapMin,
    border: 'none',
    background: 'transparent',
    color: colors.ink,
    fontSize: 15,
    fontWeight: 500,
    textDecoration: 'underline',
    textUnderlineOffset: '3px',
    '&:hover:not(:disabled)': { background: 'transparent' }
  },
  cardTriggerOpen: {
    borderColor: '#cbd5e1',
    '& $cardTriggerChevron': { transform: 'rotate(180deg)' }
  },
  cardTriggerChevron: {
    fontSize: 20,
    marginLeft: 6,
    color: '#64748b',
    transition: `transform ${motion.base} ease`
  },
  payDivider: {
    display: 'flex',
    alignItems: 'center',
    margin: '14px 0',
    '&::before, &::after': {
      content: '""',
      flex: 1,
      borderBottom: '1px solid #eef0f3'
    },
    '& span': {
      padding: '0 14px',
      fontFamily: "Poppins, 'Helvetica Neue', sans-serif",
      fontSize: 12,
      color: '#94a3b8',
      textTransform: 'uppercase',
      letterSpacing: '0.06em'
    }
  },
  cardCta: {
    width: '100%',
    height: 54,
    borderRadius: 12,
    border: 'none',
    background: '#37b45a',
    color: '#ffffff',
    fontFamily: "Poppins, 'Helvetica Neue', sans-serif",
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(55,180,90,0.20)',
    transition: 'background 0.15s ease, transform 0.12s ease, box-shadow 0.15s ease',
    '&:hover:not(:disabled)': {
      background: '#2d9c4d',
      boxShadow: '0 6px 16px rgba(55,180,90,0.28)'
    },
    '&:active:not(:disabled)': {
      transform: 'scale(0.99)'
    }
  },
  cardCtaDisabled: {
    background: '#cbd5e1 !important',
    boxShadow: 'none !important',
    cursor: 'not-allowed !important'
  },
  btnSpinner: {
    color: '#ffffff !important',
    marginLeft: 12
  },
  whatsappOptinRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 4,
    margin: '8px 0 18px',
    padding: '10px 12px',
    background: '#f8fafc',
    border: '1px solid #eef0f3',
    borderRadius: 10,
    cursor: 'pointer'
  },
  whatsappOptinLabel: {
    flex: 1,
    fontFamily: "Poppins, 'Helvetica Neue', sans-serif",
    fontSize: 13,
    color: '#475569',
    lineHeight: 1.45,
    paddingTop: 6,
    '& strong': { color: '#0f172a', fontWeight: 600 }
  },
  termsCheckbox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    fontFamily: "Poppins, 'Helvetica Neue', sans-serif",
    fontSize: 13,
    color: '#475569',
    margin: '0 0 14px',
    lineHeight: 1.5,
    cursor: 'pointer',
    '& input[type="checkbox"]': {
      width: 18,
      height: 18,
      marginTop: 2,
      accentColor: '#2ABFF3',
      cursor: 'pointer',
      flexShrink: 0
    },
    '& a': {
      color: '#2ABFF3',
      textDecoration: 'none',
      fontWeight: 500
    },
    '& a:hover': {
      textDecoration: 'underline'
    }
  },
  // #94a3b8 on white is 2.7:1. This is customer-visible text on the payment
  // panel and DESIGN.md §7 puts the floor at 4.5:1; --mute is 5.9:1 on
  // --paper. 12px went to 13 with it (--fs-helper): the smallest type on the
  // site should not be the type that explains when we take money.
  secureNote: {
    fontFamily: fonts.body,
    fontSize: 13,
    lineHeight: '18px',
    color: colors.mute,
    margin: `${space(12)}px 0 0`,
    textAlign: 'center'
  },

  // The line under the primary CTA (spec §3, placement 2). It carries the
  // promise at the one moment it is load-bearing, and it is dark enough to be
  // read: the button above it no longer claims to take the money, so this is
  // where the customer finds out what the tap actually does.
  // "Total £95" right above the pay buttons on Book & pay.
  //
  // AND IT STAYS ON SCREEN WHILE THEY PAY (2 Nov 2026). "Right above the pay
  // buttons" is true of the document and false of the glass: tapping "Or pay
  // by card" opens the card form BETWEEN this line and "Book my wash, £X",
  // and that form is 390 to 470 high (the email box, then Stripe's Payment
  // Element, which is 284 here and 362 in CI, where Stripe asks for a
  // country and a postcode as well). Measured on 2 Nov at c638c03a: on an
  // iPhone 14 the button lands 621px below this line, which puts the total
  // 342px above the top of the screen at the moment of the press; on a 1280
  // x 720 desktop it scraped in with 39px to spare, which is why CI went red
  // on a Payment Element one row taller and nothing in the commit that
  // carried it. The amount a customer is about to be charged cannot be the
  // thing that scrolls away, so the line pins instead: under the booking
  // stepper (space(12) twice round a tapMin row, plus its rule: 69), over
  // the card form, in the flow and so still above the wallet when nothing
  // has scrolled. Its own padding is taken back out of its margins, so
  // nothing below it moves.
  payTotal: {
    position: 'sticky',
    top: space(12) * 2 + sizes.tapMin + 1,
    zIndex: 2,
    background: colors.paper,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    padding: `${space(8)}px 0`,
    margin: `-${space(8)}px 0 ${space(4)}px`,
    fontFamily: fonts.body,
    fontSize: 16,
    lineHeight: '24px',
    fontWeight: 600,
    color: colors.ink,
    // A short runway of paper below it, so what slides under the pinned line
    // fades out instead of being cut through the middle of its letters. On
    // paper, over paper, it is invisible until something scrolls into it.
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      top: '100%',
      height: space(8),
      background: `linear-gradient(${colors.paper}, rgba(255, 255, 255, 0))`,
      pointerEvents: 'none'
    }
  },
  payTotalValue: { fontSize: 20 },
  // The large-car and same-day lines under the total (lib/price-lines.js).
  payTotalLines: {
    listStyle: 'none',
    padding: 0,
    margin: `-${space(8)}px 0 ${space(12)}px`,
    fontFamily: fonts.body,
    fontSize: 14,
    lineHeight: '20px',
    color: colors.mute,
    '& li': { display: 'flex', justifyContent: 'space-between' }
  },
  ctaHelper: {
    fontFamily: fonts.body,
    fontSize: 13,
    lineHeight: '18px',
    color: colors.mute,
    margin: `${space(8)}px 0 0`,
    textAlign: 'center'
  },
  // A failed card confirm under the Book button (one-checkout.js): the
  // bank's refusal or Stripe's card error, the form kept open above it.
  cardError: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: space(8),
    margin: `${space(8)}px 0 0`,
    color: colors.error,
    fontFamily: fonts.body,
    fontSize: 13,
    fontWeight: 500,
    lineHeight: '18px',
    '& > svg': { flexShrink: 0, marginTop: 1, width: 16, height: 16 }
  },
  // "Secure payment by Stripe" above the wallet row: ctaHelper's type, its
  // margin below instead of above, the lock beside it. Declared after
  // ctaHelper so this margin wins.
  securePayment: {
    margin: `0 0 ${space(8)}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },

  bottomNav: {
    paddingTop: 24,
    borderTop: '1px solid #eef0f3',
    marginTop: 24
  },
  backLink: {
    fontFamily: "Poppins, 'Helvetica Neue', sans-serif",
    fontSize: 14,
    fontWeight: 500,
    color: '#64748b',
    textDecoration: 'none',
    '&:hover': { color: '#0f172a' }
  }
})
